import React, { useEffect, useRef, useState } from 'react'

// Framer-free re-implementation of the old framer-motion FadeIn/FadeInStagger.
// Each FadeIn fades/rises (opacity 0→1, 2px translate) the first time it nears
// the viewport, honoring prefers-reduced-motion. Server-rendered HTML ships
// fully visible — the hidden state is only applied after hydration, so users
// without JS never lose content. FadeInStagger is now a plain passthrough:
// its children each animate as they scroll in, which reads the same as the old
// staggerChildren without the animation-library dependency.

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export function FadeIn({ style, ...props }) {
  const ref = useRef(null)
  // 'ssr' → rendered visible (server + first client paint), 'hidden' → armed,
  // 'visible' → transitioned in.
  const [phase, setPhase] = useState('ssr')

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      setPhase('visible')
      return undefined
    }
    setPhase('hidden')
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setPhase('visible')
          observer.disconnect()
        }
      },
      { rootMargin: '0px 0px -200px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const hidden = phase === 'hidden'
  return (
    <div
      ref={ref}
      {...props}
      style={{
        ...style,
        opacity: hidden ? 0 : 1,
        transform: hidden ? 'translateY(2px)' : 'none',
        transition: 'opacity 0.3s ease-in, transform 0.3s ease-in',
      }}
    />
  )
}

export function FadeInStagger({ faster = false, ...props }) {
  return <div {...props} />
}
